import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { API_URL, getToken, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import BackupExcelButton from "@/components/BackupExcelButton";
import { Download, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { ABSENSI_STATUS } from "@/lib/constants";
import { MatrixBulanan, MatrixTahunan } from "@/components/AbsensiMatrix";

function StatusBadge({ status }) {
  if (status === "belum_absen") {
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-slate-100 text-slate-700 border-slate-300">Belum Absen</span>;
  }
  const s = ABSENSI_STATUS.find((x) => x.v === status);
  if (!s) return <span className="pp-badge-danger">{status === "tidak_hadir" ? "Tidak Hadir" : status || "—"}</span>;
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}>{s.label}</span>;
}

const TODAY = new Date().toISOString().slice(0, 10);
const F_EMPTY = { tanggal: TODAY, bulan: "", siswa_status: "", tahun: "", siswa: "", guru: "", status: "" };

const STAT_STYLES = {
  hadir: "bg-emerald-50 text-emerald-800 border-emerald-200",
  sakit: "bg-sky-50 text-sky-800 border-sky-200",
  izin: "bg-amber-50 text-amber-800 border-amber-200",
  alfa: "bg-rose-50 text-rose-800 border-rose-200",
  bdr: "bg-violet-50 text-violet-800 border-violet-200",
  belum_absen: "bg-slate-50 text-slate-800 border-slate-300",
};

export function AbsensiSiswaKepsek() {
  const [all, setAll] = useState([]);
  const [siswaAll, setSiswaAll] = useState([]);
  const [pendMap, setPendMap] = useState({});
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: abs }, { data: siswa }, { data: pend }] = await Promise.all([
        api.get("/absensi"),
        api.get("/siswa"),
        api.get("/pendampingan"),
      ]);
      setAll(abs);
      setSiswaAll(siswa || []);
      const map = {};
      for (const p of pend || []) map[p.siswa_id] = p.nama_guru || "—";
      setPendMap(map);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Hapus data absensi ini?")) return;
    try { await api.delete(`/absensi/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/absensi/${editItem.id}`, { siswa_id: editItem.siswa_id, tanggal: editItem.tanggal, status: editItem.status });
      toast.success("Absensi diperbarui");
      setEditItem(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const siswaAktif = useMemo(() => siswaAll.filter((s) => (s.status || "aktif") === "aktif"), [siswaAll]);

  // Statistik absensi hari ini (independen dari filter) — hanya siswa aktif
  const todayStats = useMemo(() => {
    const activeIds = new Set(siswaAktif.map((s) => s.id));
    const counts = { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 };
    const absentToday = new Set();
    for (const a of all) {
      if (a.tanggal === TODAY && activeIds.has(a.siswa_id)) {
        absentToday.add(a.siswa_id);
        if (counts[a.status] !== undefined) counts[a.status] += 1;
      }
    }
    const belum_absen = siswaAktif.filter((s) => !absentToday.has(s.id)).length;
    return { ...counts, belum_absen };
  }, [all, siswaAktif]);

  // Tanggal yang dipakai untuk perhitungan "belum absen" (default hari ini)
  const belumAbsenTanggal = applied.tanggal || TODAY;

  // Rows sintetis untuk siswa yang belum absen pada tanggal tersebut
  const belumAbsenRows = useMemo(() => {
    if (applied.status !== "belum_absen") return [];
    const filledIds = new Set(all.filter((a) => a.tanggal === belumAbsenTanggal).map((a) => a.siswa_id));
    return siswaAktif
      .filter((s) => !filledIds.has(s.id))
      .map((s) => ({
        id: `belum-${s.id}-${belumAbsenTanggal}`,
        tanggal: belumAbsenTanggal,
        siswa_id: s.id,
        nama_siswa: s.nama,
        nama_guru: pendMap[s.id] || "—",
        status: "belum_absen",
      }));
  }, [applied.status, all, siswaAktif, pendMap, belumAbsenTanggal]);

  const items = useMemo(() => {
    // Filter status = belum_absen → gunakan rows sintetis + terapkan filter lain
    if (applied.status === "belum_absen") {
      return belumAbsenRows.filter((a) => {
        if (applied.siswa && !(a.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
        if (applied.guru && !(a.nama_guru || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
        return true;
      });
    }
    return all.filter((a) => {
      const useTanggal = applied.tanggal && !applied.bulan && !applied.tahun;
      if (useTanggal && a.tanggal !== applied.tanggal) return false;
      if (applied.bulan && (a.tanggal || "").slice(5, 7) !== applied.bulan) return false;
      if (applied.tahun && (a.tanggal || "").slice(0, 4) !== applied.tahun) return false;
      if (applied.siswa && !(a.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
      if (applied.guru && !(a.nama_guru || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
      if (applied.status && a.status !== applied.status) return false;
      return true;
    });
  }, [applied, all, belumAbsenRows]);

  const downloadPdf = () => {
    // "Belum Absen" hanya view UI — tidak diekspor via endpoint absensi PDF
    if (applied.status === "belum_absen") {
      alert("Ekspor PDF tidak tersedia untuk filter 'Belum Absen'. Silakan pilih status lain.");
      return;
    }
    const params = new URLSearchParams();
    const useTanggal = applied.tanggal && !applied.bulan && !applied.tahun;
    if (useTanggal) params.set("tanggal", applied.tanggal);
    if (applied.bulan) params.set("bulan", applied.bulan);
    if (applied.tahun) params.set("tahun", applied.tahun);
    if (applied.status) params.set("status", applied.status);
    if (applied.siswa || applied.guru) {
      const sids = [...new Set(items.map((it) => it.siswa_id))];
      sids.forEach((id) => params.append("siswa_ids", id));
    }
    const url = `${API_URL}/export/absensi?${params.toString()}`;
    const t = getToken();
    fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `rekap-absensi-${applied.bulan || "semua"}-${applied.tahun || "semua"}.pdf`;
        a.click(); URL.revokeObjectURL(u);
      });
  };

  const STAT_ITEMS = [
    ...ABSENSI_STATUS.map((s) => ({ v: s.v, label: s.label })),
    { v: "belum_absen", label: "Belum Absen" },
  ];

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Absensi Siswa" action={
        <div className="flex gap-2">
          <BackupExcelButton endpoint="absensi" filters={{ tanggal: applied.tanggal, bulan: applied.bulan, tahun: applied.tahun, status: applied.status !== "belum_absen" ? applied.status : "" }} testId="backup-absensi-btn" />
          <button onClick={downloadPdf} className="pp-btn-secondary" data-testid="absensi-download-pdf">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      } />

      {/* Statistik Absensi Hari Ini */}
      <section className="mb-6" data-testid="absensi-today-stats">
        <div className="mb-2 text-sm text-slate-600 font-medium">
          Statistik absensi hari ini — {formatTanggal(TODAY)}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAT_ITEMS.map((s) => (
            <div
              key={s.v}
              className={`pp-card p-4 border ${STAT_STYLES[s.v] || ""}`}
              data-testid={`absensi-today-stat-${s.v}`}
            >
              <div className="text-xs uppercase tracking-wider opacity-70">{s.label}</div>
              <div className="text-3xl font-bold mt-1">{todayStats[s.v] || 0}</div>
              <div className="text-[11px] opacity-70 mt-1">siswa</div>
            </div>
          ))}
        </div>
      </section>

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters({ ...F_EMPTY, tanggal: "" }); setApplied({ ...F_EMPTY, tanggal: "" }); }}
        dataTestidPrefix="absensi-filter"
        fields={[
          { key: "tanggal", type: "tanggal", label: "Tanggal" },
          { key: "bulan", type: "bulan", label: "Bulan" },
          { key: "siswa_status", type: "select", label: "Status Siswa", options: [
            { v: "aktif", label: "Siswa Aktif" },
            { v: "tidak_aktif", label: "Siswa Tidak Aktif" },
          ] },
          { key: "tahun", type: "tahun", label: "Tahun" },
          { key: "siswa", type: "text", label: "Nama Siswa", placeholder: "Cari nama siswa" },
          { key: "guru", type: "text", label: "Nama Guru", placeholder: "Cari nama guru" },
          { key: "status", type: "select", label: "Status", options: [
            ...ABSENSI_STATUS.map((s) => ({ v: s.v, label: s.label })),
            { v: "belum_absen", label: "Belum Absen" },
          ] },
        ]}
      />

      {applied.status === "belum_absen" && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="belum-absen-hint">
          Menampilkan siswa aktif yang <b>belum diabsen</b> pada tanggal <b>{formatTanggal(belumAbsenTanggal)}</b>.
        </div>
      )}

      {applied.bulan ? (
        <MatrixBulanan
          absensi={all}
          siswaAll={siswaAll}
          bulan={applied.bulan}
          tahun={applied.tahun || String(new Date().getFullYear())}
          siswaStatus={applied.siswa_status}
          siswaSearch={applied.siswa}
        />
      ) : applied.tahun ? (
        <MatrixTahunan
          absensi={all}
          siswaAll={siswaAll}
          tahun={applied.tahun}
          siswaStatus={applied.siswa_status}
          siswaSearch={applied.siswa}
        />
      ) : (
        <section className="pp-card pp-freeze overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Tanggal</th>
              <th className="text-left px-5 py-3">Nama Siswa</th>
              <th className="text-left px-5 py-3">Guru Pendamping</th>
              <th className="text-left px-5 py-3">Keterangan</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic">
                  {applied.status === "belum_absen" ? "Semua siswa sudah diabsen pada tanggal ini." : "Belum ada data absensi."}
                </td></tr>
              )}
              {items.map((a) => (
                <tr key={a.id} className="pp-row">
                  <td className="px-5 py-3 font-mono text-xs">{formatTanggal(a.tanggal)}</td>
                  <td className="px-5 py-3 font-medium">{a.nama_siswa}</td>
                  <td className="px-5 py-3 text-slate-600">{a.nama_guru}</td>
                  <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {a.status !== "belum_absen" && (
                      <>
                        <button onClick={() => setEditItem({ ...a })} className="pp-btn-ghost text-xs mr-1" data-testid={`absensi-edit-${a.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
                        <button onClick={() => del(a.id)} className="pp-btn-danger" data-testid={`absensi-del-${a.id}`}><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {editItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="absensi-edit-modal">
          <div className="pp-card p-6 w-full max-w-md animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Edit Absensi — {editItem.nama_siswa}</h3>
              <button onClick={() => setEditItem(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <label className="pp-label">Tanggal</label>
                <input type="date" className="pp-input" value={editItem.tanggal} onChange={(e) => setEditItem({ ...editItem, tanggal: e.target.value })} data-testid="absensi-edit-tanggal" />
              </div>
              <div>
                <label className="pp-label">Status Kehadiran</label>
                <select className="pp-input" value={editItem.status} onChange={(e) => setEditItem({ ...editItem, status: e.target.value })} data-testid="absensi-edit-status">
                  {ABSENSI_STATUS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="pp-btn-secondary">Batal</button>
                <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="absensi-edit-save">{saving ? "Menyimpan…" : "Simpan"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
